import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { toast } from 'src/@/components/ui/use-toast';
import mktAccountsService from 'src/services/mktAccountsService';
import { WHATSAPP_PROFILE_DESIGN as d, WHATSAPP_VERTICALS } from './WhatsappBusinessProfile.design';

const ABOUT_MAX = 139;
const DESCRIPTION_MAX = 512;
const ADDRESS_MAX = 256;
const EMAIL_MAX = 128;

function emptyForm() {
    return {
        about: '',
        description: '',
        address: '',
        email: '',
        vertical: 'OTHER',
        website1: '',
        website2: '',
    };
}

function apiErrorMessage(err, fallback) {
    return err?.response?.data?.message
        || err?.message
        || fallback;
}

function isValidHttpUrl(value) {
    try {
        const u = new URL(String(value).trim());
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
        return Boolean(u.hostname) && u.hostname.includes('.');
    } catch {
        return false;
    }
}

function isValidEmail(value) {
    const email = String(value || '').trim();
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function WhatsappBusinessProfile() {
    const { t } = useTranslation(['settings', 'common']);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const accountId = searchParams.get('accountId');
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [error, setError] = useState(null);
    const [phone, setPhone] = useState({ verified_name: '', display_phone_number: '' });
    const [pictureUrl, setPictureUrl] = useState('');
    const [form, setForm] = useState(emptyForm());

    const voltar = useCallback(() => {
        navigate('/dashboard/configuracoes/contas-sociais');
    }, [navigate]);

    const carregar = useCallback(async () => {
        if (!accountId) {
            setError(t('socialAccounts.profile.accountMissing'));
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await mktAccountsService.getBusinessProfile(accountId);
            if (res?.error) {
                throw new Error(res.message || t('socialAccounts.profile.loadFailed'));
            }
            const data = res.data || {};
            const profile = data.profile || {};
            const websites = Array.isArray(profile.websites) ? profile.websites : [];
            setPhone(data.phone || {});
            setPictureUrl(profile.profile_picture_url || '');
            setForm({
                about: profile.about || '',
                description: profile.description || '',
                address: profile.address || '',
                email: profile.email || '',
                vertical: WHATSAPP_VERTICALS.some((v) => v.value === profile.vertical)
                    ? profile.vertical
                    : 'OTHER',
                website1: websites[0] || '',
                website2: websites[1] || '',
            });
        } catch (err) {
            setError(apiErrorMessage(err, t('socialAccounts.profile.metaError')));
        } finally {
            setLoading(false);
        }
    }, [accountId, t]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSalvar = async () => {
        if (!accountId) return;

        const websitesRaw = [form.website1, form.website2]
            .map((u) => String(u || '').trim())
            .filter(Boolean);
        const websites = websitesRaw.filter(isValidHttpUrl).slice(0, 2);
        if (websitesRaw.length !== websites.length) {
            toast({
                title: t('socialAccounts.whatsapp.incompleteWebsite'),
                description: t('socialAccounts.whatsapp.incompleteWebsiteDesc'),
            });
        }

        const email = String(form.email || '').trim();
        if (email && !isValidEmail(email)) {
            const msg = t('socialAccounts.profile.invalidEmailDesc');
            setError(msg);
            toast({ title: t('socialAccounts.profile.invalidEmail'), description: msg, variant: 'destructive' });
            return;
        }

        const data = {};
        const about = String(form.about || '').trim();
        const description = String(form.description || '').trim();
        const address = String(form.address || '').trim();
        const vertical = String(form.vertical || '').trim();

        if (about) data.about = about;
        if (description) data.description = description;
        if (address) data.address = address;
        if (email) data.email = email;
        if (vertical) data.vertical = vertical;
        if (websites.length) data.websites = websites;

        if (Object.keys(data).length === 0) {
            const msg = t('socialAccounts.profile.nothingToSaveDesc');
            setError(msg);
            toast({ title: t('socialAccounts.profile.nothingToSave'), description: msg, variant: 'destructive' });
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const res = await mktAccountsService.updateBusinessProfile(accountId, data);

            if (res?.error) {
                throw new Error(res.message || t('socialAccounts.profile.saveFailed'));
            }

            toast({
                title: t('socialAccounts.profile.profileUpdated'),
                description: t('socialAccounts.profile.profileUpdatedDesc'),
            });
            await carregar();
        } catch (err) {
            const msg = apiErrorMessage(err, t('socialAccounts.profile.metaError'));
            setError(msg);
            toast({ title: t('socialAccounts.profile.saveError'), description: msg, variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };

    const handlePhotoChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !accountId) return;

        if (!['image/jpeg', 'image/png', 'image/pjpeg'].includes(file.type)) {
            toast({
                title: t('socialAccounts.profile.invalidFormat'),
                description: t('socialAccounts.profile.invalidFormatDesc'),
                variant: 'destructive',
            });
            return;
        }

        setUploadingPhoto(true);
        setError(null);
        try {
            const res = await mktAccountsService.updateBusinessProfilePicture(accountId, file);
            if (res?.error) {
                throw new Error(res.message || t('socialAccounts.profile.uploadFailed'));
            }
            if (res?.data?.profile_picture_url) {
                setPictureUrl(res.data.profile_picture_url);
            } else {
                await carregar();
            }
            toast({
                title: t('socialAccounts.profile.photoUpdated'),
                description: t('socialAccounts.whatsapp.photoUpdatedDesc'),
            });
        } catch (err) {
            const msg = apiErrorMessage(err, t('socialAccounts.profile.metaError'));
            setError(msg);
            toast({ title: t('socialAccounts.profile.uploadError'), description: msg, variant: 'destructive' });
        } finally {
            setUploadingPhoto(false);
        }
    };

    const headerSubtitle = phone.display_phone_number
        || phone.verified_name
        || t('socialAccounts.whatsapp.displayNameHint');

    const verticalLabel = t(`socialAccounts.verticals.${form.vertical || 'OTHER'}`);
    const websites = [form.website1, form.website2].map((u) => String(u || '').trim()).filter(Boolean);

    const renderHeader = (actions = null) => (
        <div className={d.header}>
            <div className={d.headerInner}>
                <div className={d.topbar}>
                    <div className={d.topbarIdentity}>
                        <button type="button" className={d.backBtn} onClick={voltar} title={t('common:buttons.back')}>
                            <Iconify icon="eva:arrow-back-outline" width={18} />
                        </button>
                        <div className={d.iconTile}>
                            <Iconify icon="ic:round-whatsapp" width={22} />
                        </div>
                        <div className="min-w-0">
                            <h1 className={d.title}>{t('socialAccounts.whatsapp.title')}</h1>
                            <p className={d.subtitle}>{headerSubtitle}</p>
                        </div>
                    </div>
                    {actions ? <div className={d.actions}>{actions}</div> : null}
                </div>
            </div>
        </div>
    );

    if (!accountId) {
        return (
            <div className={d.shell} style={{ fontFamily: d.fontStack }}>
                {renderHeader()}
                <div className={d.body}>
                    <div className={d.error}>
                        <Iconify icon="eva:alert-circle-outline" width={18} className="shrink-0 mt-0.5" />
                        <span>{t('socialAccounts.profile.accountMissingHint')}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={d.shell} style={{ fontFamily: d.fontStack }}>
            {renderHeader(
                <>
                    <button type="button" className={d.actionOutline} onClick={carregar} disabled={loading || saving}>
                        <Iconify icon={loading ? 'eos-icons:loading' : 'eva:refresh-outline'} width={14} />
                        {t('socialAccounts.profile.reload')}
                    </button>
                    {!loading && (
                        <button
                            type="button"
                            className={d.actionPrimary}
                            onClick={handleSalvar}
                            disabled={saving || uploadingPhoto}
                        >
                            <Iconify icon={saving ? 'eos-icons:loading' : 'eva:checkmark-outline'} width={14} />
                            {t('common:buttons.save')}
                        </button>
                    )}
                </>
            )}

            <div className={d.body}>
                {error && (
                    <div className={d.error}>
                        <Iconify icon="eva:alert-circle-outline" width={18} className="shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                {loading ? (
                    <div className={d.loading}>
                        <Iconify icon="eos-icons:loading" width={28} className="text-emerald-600" />
                        <span className="text-[13px] font-medium">{t('socialAccounts.profile.loading')}</span>
                    </div>
                ) : (
                    <div className={d.grid}>
                        <div className={d.stack}>
                            <section className={d.card}>
                                <h2 className={d.cardTitle}>
                                    <span className={d.cardIcon}>
                                        <Iconify icon="eva:person-outline" width={16} />
                                    </span>
                                    {t('socialAccounts.whatsapp.title')}
                                </h2>
                                <div className={d.identityRow}>
                                    <div className={d.avatarWrap}>
                                        {pictureUrl ? (
                                            <img src={pictureUrl} alt="" className={d.avatar} />
                                        ) : (
                                            <div className={d.avatarFallback}>
                                                <Iconify icon="ic:round-whatsapp" width={28} className="text-emerald-600" />
                                            </div>
                                        )}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png"
                                            className="hidden"
                                            onChange={handlePhotoChange}
                                        />
                                        <button
                                            type="button"
                                            className={d.changeBtn}
                                            disabled={uploadingPhoto}
                                            onClick={() => fileInputRef.current?.click()}
                                            title={t('socialAccounts.profile.changePhoto')}
                                            aria-label={t('socialAccounts.profile.changePhoto')}
                                        >
                                            <Iconify
                                                icon={uploadingPhoto ? 'eos-icons:loading' : 'eva:camera-outline'}
                                                width={14}
                                            />
                                        </button>
                                    </div>
                                    <div className={d.identityMeta}>
                                        <p className={d.identityName}>{phone.verified_name || '—'}</p>
                                        <p className={d.identityPhone}>{phone.display_phone_number || '—'}</p>
                                    </div>
                                </div>
                                <div className={d.identityFields}>
                                    <div className={d.field}>
                                        <label className={d.label}>{t('socialAccounts.whatsapp.displayName')}</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={phone.verified_name || ''}
                                            className={d.inputReadonly}
                                        />
                                    </div>
                                    <div className={d.field}>
                                        <label className={d.label}>{t('socialAccounts.whatsapp.number')}</label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={phone.display_phone_number || ''}
                                            className={d.inputReadonly}
                                        />
                                    </div>
                                </div>
                                <div className={`${d.helpBox} mt-4`}>
                                    <Iconify icon="eva:info-outline" width={16} className="shrink-0 mt-0.5 text-amber-700" />
                                    <span>{t('socialAccounts.whatsapp.displayNameHint')}</span>
                                </div>
                            </section>

                            <section className={d.card}>
                                <h2 className={d.cardTitle}>
                                    <span className={d.cardIcon}>
                                        <Iconify icon="eva:edit-2-outline" width={16} />
                                    </span>
                                    {t('socialAccounts.whatsapp.formTitle')}
                                </h2>
                                <div className={d.formGrid}>
                                    <div className={d.fieldFull}>
                                        <div className={d.labelRow}>
                                            <label className={d.label}>{t('socialAccounts.whatsapp.about')}</label>
                                            <span className={d.counter}>{form.about.length}/{ABOUT_MAX}</span>
                                        </div>
                                        <textarea
                                            value={form.about}
                                            maxLength={ABOUT_MAX}
                                            onChange={(e) => setField('about', e.target.value)}
                                            className={d.textareaSm}
                                            placeholder={t('socialAccounts.whatsapp.aboutPlaceholder')}
                                            rows={2}
                                        />
                                    </div>

                                    <div className={d.fieldFull}>
                                        <div className={d.labelRow}>
                                            <label className={d.label}>{t('socialAccounts.whatsapp.description')}</label>
                                            <span className={d.counter}>{form.description.length}/{DESCRIPTION_MAX}</span>
                                        </div>
                                        <textarea
                                            value={form.description}
                                            maxLength={DESCRIPTION_MAX}
                                            onChange={(e) => setField('description', e.target.value)}
                                            className={d.textarea}
                                            placeholder={t('socialAccounts.whatsapp.descriptionPlaceholder')}
                                            rows={3}
                                        />
                                    </div>

                                    <div className={d.field}>
                                        <label className={d.label}>{t('socialAccounts.profile.category')}</label>
                                        <select
                                            value={form.vertical || 'OTHER'}
                                            onChange={(e) => setField('vertical', e.target.value)}
                                            className={d.select}
                                        >
                                            {WHATSAPP_VERTICALS.map((item) => (
                                                <option key={item.value} value={item.value}>
                                                    {t(`socialAccounts.verticals.${item.value}`)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={d.field}>
                                        <label className={d.label}>{t('socialAccounts.profile.email')}</label>
                                        <input
                                            type="email"
                                            value={form.email}
                                            maxLength={EMAIL_MAX}
                                            onChange={(e) => setField('email', e.target.value)}
                                            className={d.input}
                                            placeholder={t('socialAccounts.profile.emailPlaceholder')}
                                        />
                                    </div>

                                    <div className={d.fieldFull}>
                                        <div className={d.labelRow}>
                                            <label className={d.label}>{t('socialAccounts.whatsapp.address')}</label>
                                            <span className={d.counter}>{form.address.length}/{ADDRESS_MAX}</span>
                                        </div>
                                        <input
                                            type="text"
                                            value={form.address}
                                            maxLength={ADDRESS_MAX}
                                            onChange={(e) => setField('address', e.target.value)}
                                            className={d.input}
                                            placeholder={t('socialAccounts.whatsapp.addressPlaceholder')}
                                        />
                                    </div>

                                    <div className={d.field}>
                                        <label className={d.label}>{t('socialAccounts.whatsapp.website1')}</label>
                                        <input
                                            type="url"
                                            value={form.website1}
                                            onChange={(e) => setField('website1', e.target.value)}
                                            className={d.input}
                                            placeholder={t('socialAccounts.profile.websitePlaceholder')}
                                        />
                                    </div>
                                    <div className={d.field}>
                                        <label className={d.label}>{t('socialAccounts.whatsapp.website2')}</label>
                                        <input
                                            type="url"
                                            value={form.website2}
                                            onChange={(e) => setField('website2', e.target.value)}
                                            className={d.input}
                                            placeholder={t('socialAccounts.profile.websitePlaceholder')}
                                        />
                                    </div>
                                </div>
                            </section>
                        </div>

                        <aside className={d.previewCard}>
                            <div className={d.previewHeader}>
                                <Iconify icon="ic:round-whatsapp" width={18} />
                                <span className={d.previewTitle}>{t('socialAccounts.whatsapp.previewTitle', 'Pré-visualização do perfil')}</span>
                            </div>
                            <div className={d.previewBody}>
                                {pictureUrl ? (
                                    <img src={pictureUrl} alt="" className={d.previewAvatar} />
                                ) : (
                                    <div className={d.previewAvatarFallback}>
                                        <Iconify icon="ic:round-whatsapp" width={36} className="text-emerald-600" />
                                    </div>
                                )}
                                <p className={d.previewName}>{phone.verified_name || '—'}</p>
                                {form.about ? (
                                    <p className={d.previewAbout}>{form.about}</p>
                                ) : (
                                    <p className={d.previewEmpty}>{t('socialAccounts.whatsapp.noAbout', 'Sem status')}</p>
                                )}
                                {form.description ? (
                                    <p className={d.previewDescription}>{form.description}</p>
                                ) : null}
                                <span className={d.previewBadge}>{verticalLabel}</span>
                                <div className={d.previewMeta}>
                                    {phone.display_phone_number && (
                                        <div className={d.previewMetaRow}>
                                            <Iconify icon="eva:phone-outline" width={14} className={d.previewMetaIcon} />
                                            <span>{phone.display_phone_number}</span>
                                        </div>
                                    )}
                                    {form.email && (
                                        <div className={d.previewMetaRow}>
                                            <Iconify icon="eva:email-outline" width={14} className={d.previewMetaIcon} />
                                            <span className="break-all">{form.email}</span>
                                        </div>
                                    )}
                                    {form.address && (
                                        <div className={d.previewMetaRow}>
                                            <Iconify icon="eva:pin-outline" width={14} className={d.previewMetaIcon} />
                                            <span>{form.address}</span>
                                        </div>
                                    )}
                                    {websites.map((site) => (
                                        <div key={site} className={d.previewMetaRow}>
                                            <Iconify icon="eva:globe-2-outline" width={14} className={d.previewMetaIcon} />
                                            <span className="break-all">{site.replace(/^https?:\/\//, '')}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    </div>
                )}
            </div>
        </div>
    );
}
